import React, { useState } from 'react';
import { Bot, FileUp, LoaderCircle, Search, ShieldCheck } from 'lucide-react';
import { APP_NAME } from '../config/appConfig';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';
import { queryRag, RagCitation, uploadRagDocument } from '../api/ragApi';

const DocumentQA: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [citations, setCitations] = useState<RagCitation[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isQuerying, setIsQuerying] = useState(false);

  useDocumentMetadata(
    'Document QA',
    `Upload healthcare documents and ask grounded questions with ${APP_NAME}'s Groq-powered RAG assistant.`
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setStatusMessage(null);
    setErrorMessage(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorMessage('Choose a PDF or TXT file to index.');
      return;
    }

    setIsUploading(true);
    setErrorMessage(null);
    setStatusMessage(null);

    try {
      const result = await uploadRagDocument(selectedFile);
      setStatusMessage(
        `${result.fileName} indexed successfully with ${result.chunkCount} chunks. The local store now tracks ${result.storeStats.documentCount} document(s).`
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to upload the document.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleQuery = async () => {
    if (!question.trim()) {
      setErrorMessage('Enter a question to query the indexed documents.');
      return;
    }

    setIsQuerying(true);
    setErrorMessage(null);

    try {
      const result = await queryRag(question);
      setAnswer(result.answer);
      setCitations(result.citations);
    } catch (error) {
      setAnswer('');
      setCitations([]);
      setErrorMessage(error instanceof Error ? error.message : 'Unable to answer the question.');
    } finally {
      setIsQuerying(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="container mx-auto max-w-6xl px-4">
        <div className="mb-10 text-center">
          <p className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-4 py-2 text-sm font-medium text-sky-700">
            <ShieldCheck size={16} />
            Groq + local retrieval pipeline
          </p>
          <h1 className="mt-5 text-4xl font-bold text-slate-900">Document QA Assistant</h1>
          <p className="mx-auto mt-4 max-w-3xl text-lg text-slate-600">
            Upload a PDF or TXT file, index it locally, and ask grounded questions answered by Groq with explicit source citations.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
          <section className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
            <div className="flex items-center gap-3">
              <FileUp className="h-6 w-6 text-sky-600" />
              <h2 className="text-2xl font-semibold text-slate-900">Index a Document</h2>
            </div>
            <p className="mt-3 text-slate-600">
              Supported formats: PDF and TXT. The backend extracts text, chunks it, creates local embeddings, and stores the vectors for retrieval.
            </p>

            <div className="mt-6 rounded-3xl border-2 border-dashed border-sky-200 bg-sky-50/60 p-8 text-center">
              <input
                id="rag-file-upload"
                type="file"
                accept=".pdf,.txt,text/plain,application/pdf"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="rag-file-upload"
                className="cursor-pointer rounded-full bg-sky-600 px-5 py-3 font-semibold text-white transition hover:bg-sky-700"
              >
                Choose Document
              </label>
              <p className="mt-4 text-sm text-slate-500">
                {selectedFile ? selectedFile.name : 'No file selected yet.'}
              </p>
            </div>

            <button
              onClick={handleUpload}
              disabled={isUploading}
              className={`mt-6 w-full rounded-full px-6 py-3 font-semibold text-white transition ${
                isUploading ? 'cursor-not-allowed bg-slate-400' : 'bg-sky-600 hover:bg-sky-700'
              }`}
            >
              {isUploading ? 'Indexing Document...' : 'Upload and Index'}
            </button>

            {statusMessage && (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                {statusMessage}
              </div>
            )}

            {errorMessage && (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {errorMessage}
              </div>
            )}
          </section>

          <section className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
            <div className="flex items-center gap-3">
              <Bot className="h-6 w-6 text-sky-600" />
              <h2 className="text-2xl font-semibold text-slate-900">Ask a Grounded Question</h2>
            </div>
            <p className="mt-3 text-slate-600">
              Ask about diagnoses, medications, procedures, or notes within uploaded files. Answers come back with source chunks for traceability.
            </p>

            <div className="mt-6">
              <label htmlFor="rag-question" className="text-sm font-medium text-slate-700">
                Question
              </label>
              <textarea
                id="rag-question"
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                rows={4}
                placeholder="What follow-up actions are recommended in the uploaded document?"
                className="mt-2 block w-full rounded-2xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>

            <button
              onClick={handleQuery}
              disabled={isQuerying}
              className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold text-white transition ${
                isQuerying ? 'cursor-not-allowed bg-slate-400' : 'bg-slate-900 hover:bg-slate-800'
              }`}
            >
              {isQuerying ? <LoaderCircle className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
              {isQuerying ? 'Retrieving Answer...' : 'Query Documents'}
            </button>

            <div className="mt-8 rounded-3xl bg-slate-50 p-6">
              <h3 className="text-lg font-semibold text-slate-900">Answer</h3>
              <div className="mt-3 whitespace-pre-wrap text-slate-700">
                {answer || 'Your grounded answer will appear here after you upload a document and submit a question.'}
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-semibold text-slate-900">Citations</h3>
              <div className="mt-3 space-y-3">
                {citations.length ? (
                  citations.map((citation) => (
                    <div key={citation.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="font-medium text-slate-900">{citation.fileName}</p>
                        <p className="text-sm text-slate-500">
                          Chunk {citation.chunkIndex + 1} | Score {citation.score}
                        </p>
                      </div>
                      <p className="mt-2 text-sm text-slate-600">{citation.excerpt}...</p>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500">Relevant source chunks will appear here.</p>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DocumentQA;
